<script setup lang="ts">
import { BriefcaseBusiness, Mail, Rocket } from 'lucide-vue-next';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import SearchableSelectMenu from '@/components/ui/searchable-select-menu/SearchableSelectMenu.vue';
import { recommendClientOnboardingTemplateId } from '~/lib/clientOnboarding';
import { getOnboardingInvitationTiming } from '~/lib/onboardingRunState';
import { sortOnboardingStages } from '~/lib/onboardingProgress';
import { DEFAULT_ONBOARDING_TEMPLATES, resolveOnboardingTemplate } from '~/lib/onboardingDefaults';
import type { Client, Project } from '~/types/models';

export type ClientOnboardingHandoffPayload = {
    templateId: string;
    clientName: string;
    projectMode: 'existing' | 'new' | 'skip';
    projectTemplateId?: string;
    existingProjectId?: string;
    projectName: string;
    projectDescription?: string;
    ownerId?: string;
    ownerName?: string;
    sendWelcomeEmail: boolean;
};

const props = defineProps<{
    open: boolean;
    client?: Client | null;
    projects?: Project[];
    submitting?: boolean;
}>();

const emit = defineEmits<{
    (event: 'update:open', value: boolean): void;
    (event: 'start', payload: ClientOnboardingHandoffPayload): void;
}>();

const userStore = useUserStore();
const onboardingStore = useOnboardingStore();
const workflowStore = useWorkflowStore();
const subscription = useWorkspaceSubscription();
const templatesLoading = ref(false);
const templatesError = ref('');
const projectTemplateOptions = computed(() => [
    { value: 'blank', label: 'Blank project (no template)' },
    ...workflowStore.templates.filter(template => !template.isArchived).map(template => ({
        value: template.id,
        label: template.name,
        description: `${template.milestones.length} milestones · ${template.tasks.length} starter tasks`,
    })),
]);
const selectedProjectTemplate = computed(() => workflowStore.getTemplateById(state.value.projectTemplateId));
const projectModeOptions = computed(() => [
    ...(props.projects?.length ? [{ value: 'existing', label: 'Use an existing project (skip creation)' }] : []),
    { value: 'new', label: 'Create a new project' },
    { value: 'skip', label: 'Skip project creation for now' },
]);

const isOpen = computed({
    get: () => props.open,
    set: (value: boolean) => emit('update:open', value),
});

const recommendedTemplateId = computed(() => recommendClientOnboardingTemplateId({
    client: props.client,
    projectNames: (props.projects || []).map((project) => project.name),
}));

const templateOptions = computed(() => onboardingStore.templates
    .filter((template) => !template.isArchived)
    .map((template) => ({
        value: template.id,
        label: template.id === recommendedTemplateId.value
            ? `${template.name} (recommended)`
            : template.isDefault
                ? `${template.name} (default)`
                : template.name,
    })));

const projectOptions = computed(() => (props.projects || []).map((project) => ({
    value: project.id,
    label: project.name,
})));

const selectedOnboardingTemplate = computed(() => onboardingStore.templates.find(template => template.id === state.value.templateId)
    || resolveOnboardingTemplate(state.value.templateId));
const invitationTiming = computed(() => getOnboardingInvitationTiming(selectedOnboardingTemplate.value));
const pipelineSequence = computed(() => sortOnboardingStages(selectedOnboardingTemplate.value.stages).map(stage => stage.label).join(' → '));
const portalInvitationSummary = computed(() => invitationTiming.value.canInviteAtStart
    ? state.value.sendWelcomeEmail ? 'Send portal invite now' : 'Invite later from client access'
    : invitationTiming.value.portalStage ? `Portal invite at ${invitationTiming.value.portalStage.label}` : 'Portal invitation managed separately');

const recommendedTemplateName = computed(() => onboardingStore.templates.find((template) => template.id === state.value.templateId)?.name
    || DEFAULT_ONBOARDING_TEMPLATES.find((template) => template.id === state.value.templateId)?.name
    || 'General client onboarding');

const clientPrimaryEmail = computed(() => {
    const contacts = Array.isArray(props.client?.contacts) ? props.client.contacts : [];
    const primaryContact = contacts.find((contact) => contact?.isPrimary && String(contact.email || '').trim());
    const fallbackContact = contacts.find((contact) => String(contact?.email || '').trim());
    return String(primaryContact?.email || fallbackContact?.email || props.client?.email || '').trim();
});

const state = ref({
    templateId: DEFAULT_ONBOARDING_TEMPLATES[0]!.id,
    clientName: '',
    projectMode: 'new' as 'existing' | 'new' | 'skip',
    projectTemplateId: 'blank',
    existingProjectId: '',
    projectName: '',
    projectDescription: '',
    ownerName: '',
    sendWelcomeEmail: true,
});

function defaultProjectName(): string {
    const existingProject = (props.projects || [])[0];
    if (existingProject?.name) {
        return existingProject.name;
    }

    const displayName = String(
        props.client?.companyName
        || props.client?.businessName
        || props.client?.name
        || props.client?.contactName
        || '',
    ).trim();

    return displayName ? `${displayName} onboarding project` : 'New client onboarding project';
}

function syncState() {
    const clientName = String(
        props.client?.name
        || props.client?.companyName
        || props.client?.businessName
        || props.client?.contactName
        || '',
    ).trim();
    const existingProject = (props.projects || [])[0];

    state.value = {
        templateId: recommendedTemplateId.value,
        clientName,
        projectMode: existingProject ? 'existing' : 'new',
        projectTemplateId: 'blank',
        existingProjectId: existingProject?.id || '',
        projectName: existingProject?.name || defaultProjectName(),
        projectDescription: String(
            props.client?.lifecycle?.notes
            || existingProject?.description
            || existingProject?.portalSummary
            || '',
        ).trim(),
        ownerName: String(
            userStore.user?.displayName
            || userStore.user?.fullName
            || '',
        ).trim(),
        sendWelcomeEmail: Boolean(clientPrimaryEmail.value),
    };
}

watch(() => props.open, async (open) => {
    if (!open) return;
    syncState();
    templatesLoading.value = true;
    templatesError.value = '';
    try {
        await Promise.all([
            onboardingStore.fetchTemplates(),
            subscription.canUseWorkflows.value ? workflowStore.fetchTemplates() : Promise.resolve(),
        ]);
        templatesError.value = subscription.canUseWorkflows.value ? workflowStore.error || '' : '';
    } finally {
        templatesLoading.value = false;
    }
});

watch(() => props.client?.id, () => {
    if (props.open) {
        syncState();
    }
});

function handleSubmit() {
    emit('start', {
        templateId: state.value.templateId,
        clientName: state.value.clientName.trim(),
        projectMode: state.value.projectMode,
        projectTemplateId: state.value.projectMode === 'new' && state.value.projectTemplateId !== 'blank'
            ? state.value.projectTemplateId : undefined,
        existingProjectId: state.value.projectMode === 'existing'
            ? state.value.existingProjectId || undefined
            : undefined,
        projectName: state.value.projectMode === 'existing'
            ? ((props.projects || []).find((project) => project.id === state.value.existingProjectId)?.name || state.value.projectName).trim()
            : state.value.projectMode === 'new' ? state.value.projectName.trim() : '',
        projectDescription: state.value.projectDescription.trim() || undefined,
        ownerId: userStore.userId || undefined,
        ownerName: state.value.ownerName.trim() || undefined,
        sendWelcomeEmail: invitationTiming.value.canInviteAtStart && state.value.sendWelcomeEmail,
    });
}
</script>

<template>
    <Dialog v-model:open="isOpen">
        <DialogContent class="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
            <DialogHeader>
                <DialogTitle>Start onboarding handoff</DialogTitle>
                <DialogDescription>
                    Start this client’s onboarding pipeline with the right project context. Portal invitation timing follows the selected pipeline.
                </DialogDescription>
            </DialogHeader>

            <form class="space-y-5" @submit.prevent="handleSubmit">
                <div class="grid gap-4 md:grid-cols-2">
                    <div class="space-y-2">
                        <Label for="handoff-client-name">Client name</Label>
                        <Input id="handoff-client-name" v-model="state.clientName" required placeholder="Northstar Studio" />
                    </div>
                    <div class="space-y-2">
                        <Label for="handoff-owner-name">Account manager</Label>
                        <Input id="handoff-owner-name" v-model="state.ownerName" placeholder="Team member" />
                    </div>
                </div>

                <div class="space-y-2">
                    <div class="flex items-center justify-between gap-3">
                        <Label>Onboarding pipeline</Label>
                        <span class="text-xs text-muted-foreground">Recommended: {{ recommendedTemplateName }}</span>
                    </div>
                    <SearchableSelectMenu
                        v-model="state.templateId"
                        :options="templateOptions"
                        placeholder="Choose onboarding pipeline"
                        search-placeholder="Search pipelines..."
                        show-all-by-default
                        variant="form"
                    />
                </div>

                <div class="space-y-2 border-t border-border/60 pt-4">
                    <p class="text-sm font-medium text-foreground">Pipeline order</p>
                    <p class="text-sm leading-6 text-muted-foreground">{{ pipelineSequence }}</p>
                    <p v-if="invitationTiming.firstActionStage" class="text-sm text-foreground">Starts with {{ invitationTiming.firstActionStage.label }}.</p>
                </div>

                <div class="space-y-2">
                    <Label for="handoff-project-mode">Project setup</Label>
                    <SearchableSelectMenu
                        id="handoff-project-mode"
                        v-model="state.projectMode"
                        :options="projectModeOptions"
                        show-all-by-default
                        variant="form"
                    />
                </div>

                <div v-if="state.projectMode === 'existing'" class="space-y-2">
                    <Label>Choose project</Label>
                    <SearchableSelectMenu
                        v-model="state.existingProjectId"
                        :options="projectOptions"
                        placeholder="Choose an existing project"
                        search-placeholder="Search projects..."
                        show-all-by-default
                        variant="form"
                    />
                </div>

                <div v-else-if="state.projectMode === 'new'" class="space-y-2">
                    <Label for="handoff-project-name">Project name</Label>
                    <Input id="handoff-project-name" v-model="state.projectName" required placeholder="Website redesign" />
                </div>

                <div v-if="state.projectMode === 'new'" class="space-y-2">
                    <Label>Project template</Label>
                    <SearchableSelectMenu
                        v-model="state.projectTemplateId"
                        :options="projectTemplateOptions"
                        :disabled="submitting || templatesLoading || !subscription.canUseWorkflows.value"
                        :placeholder="templatesLoading ? 'Loading project templates…' : 'Choose a project template'"
                        search-placeholder="Search project templates..."
                        show-all-by-default
                        variant="form"
                    />
                    <p v-if="templatesError" class="text-sm text-destructive">{{ templatesError }}</p>
                    <p v-else-if="!subscription.canUseWorkflows.value" class="text-sm text-muted-foreground">Project templates are available on the Growth plan and above.</p>
                    <p v-else-if="selectedProjectTemplate" class="text-sm text-muted-foreground">
                        Copies this template’s milestones and starter tasks. Quotes can be created separately after onboarding starts.
                    </p>
                    <p v-else-if="!templatesLoading && projectTemplateOptions.length === 1" class="text-sm text-muted-foreground">No project templates yet. Create one in Workflows &amp; Templates, or start with a blank project.</p>
                </div>
                <p v-if="state.projectMode === 'skip'" class="text-sm text-muted-foreground">
                    Onboarding will stay with this client. No project will be created.
                </p>

                <div v-if="state.projectMode !== 'skip'" class="space-y-2">
                    <Label for="handoff-project-description">Project context</Label>
                    <Textarea
                        id="handoff-project-description"
                        v-model="state.projectDescription"
                        rows="4"
                        placeholder="Capture the scope, discovery notes, and what the team should know before kickoff."
                    />
                </div>

                <div v-if="invitationTiming.canInviteAtStart" class="border-t border-border/60 pt-4">
                    <label class="flex items-start gap-3">
                        <Checkbox
                            :checked="state.sendWelcomeEmail"
                            :disabled="!clientPrimaryEmail"
                            class="mt-0.5"
                            @update:checked="state.sendWelcomeEmail = $event === true"
                        />
                        <span class="space-y-1">
                            <span class="flex items-center gap-2 text-sm font-medium text-foreground">
                                <Mail class="size-4" />
                                Send portal welcome email now
                            </span>
                            <span class="block text-sm text-muted-foreground">
                                Portal activation is the first actionable stage in this pipeline. The primary contact receives their invitation now.
                            </span>
                            <span v-if="!clientPrimaryEmail" class="block text-sm text-destructive">
                                Add a primary email before sending portal access.
                            </span>
                        </span>
                    </label>
                </div>

                <div v-else class="space-y-2 border-t border-border/60 pt-4">
                    <p class="flex items-center gap-2 text-sm font-medium text-foreground"><Mail class="size-4" />Portal invitation</p>
                    <p v-if="invitationTiming.portalStage" class="text-sm leading-6 text-muted-foreground">
                        No portal invitation is sent when onboarding starts. Complete the earlier pipeline requirements, then invite the client from client access at {{ invitationTiming.portalStage.label }}.
                    </p>
                    <p v-else class="text-sm leading-6 text-muted-foreground">
                        This pipeline has no portal activation requirement. Invite the client separately from client access when appropriate.
                    </p>
                </div>

                <div class="grid gap-3 border-t border-border/60 pt-4 text-sm text-muted-foreground sm:grid-cols-3">
                    <div class="flex gap-2">
                        <BriefcaseBusiness class="mt-0.5 size-4 text-primary" />
                        <span>Keep work with this client</span>
                    </div>
                    <div class="flex gap-2">
                        <Rocket class="mt-0.5 size-4 text-primary" />
                        <span>Create onboarding run</span>
                    </div>
                    <div class="flex gap-2">
                        <Mail class="mt-0.5 size-4 text-primary" />
                        <span>{{ portalInvitationSummary }}</span>
                    </div>
                </div>

                <DialogFooter>
                    <Button type="button" variant="outline" :disabled="submitting" @click="isOpen = false">
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        :disabled="submitting || templatesLoading || !state.clientName.trim() || (state.projectMode === 'new' && !state.projectName.trim()) || (state.projectMode === 'existing' && !state.existingProjectId)"
                    >
                        {{ submitting ? 'Starting...' : 'Start onboarding' }}
                    </Button>
                </DialogFooter>
            </form>
        </DialogContent>
    </Dialog>
</template>
